import React, { useState } from "react";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import EditSellingPriceModal from "./EditSellingPriceModal";
import { masterService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";

export default function ProductSellingPricesSection({ product, onRefresh }) {
  const { showSuccess, showError } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);

  const prices = product?.selling_prices || [];

  const handleOpenAdd = () => {
    setSelectedPrice(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setSelectedPrice(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus varian harga '${item.name}'?`)) {
      return;
    }
    try {
      await masterService.products.prices.delete(product.id, item.id);
      showSuccess("Varian harga jual berhasil dihapus");
      onRefresh();
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Gagal menghapus varian harga jual");
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 lg:p-8">
      {/* Header Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-6">
        <div>
          <h3 className="text-gray-900 text-[18px] font-bold">Varian Harga Jual</h3>
          <p className="text-xs text-slate-400 font-medium mt-0.5">
            Harga utama otomatis mengikuti data produk. Tambahkan varian harga jual jika diperlukan.
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpenAdd}
          className="flex items-center justify-center gap-2 px-5 py-2 bg-white border border-teal-500 text-teal-600 rounded-xl hover:bg-teal-50 font-semibold text-sm transition-colors shadow-sm w-full sm:w-auto shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} /> Tambah Harga
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">
                No
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Nama Harga
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Harga Jual
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center w-24">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-900 font-semibold">
            {prices.length > 0 ? (
              prices.map((item, idx) => {
                const isActive = item.status === "ACTIVE";
                return (
                  <tr key={item.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-4 text-center text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-4">{item.name}</td>
                    <td className="px-4 py-4">
                      <span className="text-teal-700 font-bold">{formatCurrency(item.price)}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          isActive
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {isActive ? "Aktif" : "Non-Aktif"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(item)}
                          className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-md transition-colors cursor-pointer"
                          title="Edit Harga"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                          title="Hapus Harga"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="border-b border-slate-50">
                <td colSpan="5" className="py-8 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
                    <Tag className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">Belum ada varian harga jual tambahan</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Tambah/Edit */}
      <EditSellingPriceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        priceItem={selectedPrice}
        product={product}
        onSuccess={onRefresh}
      />
    </div>
  );
}
